import { inject, injectable } from "@launchtray/tsyringe-async";
import { Bindable, nameof } from "bindable-data";
import { IAuthModel } from "./IAuthModel";
import { INetworkClient } from "../../../../networking/INetworkClient";
import { IGuestModel } from "../../../common/models/IGuestModel";
import { AuthDto } from "../../../../networking/data/responses/auth/AuthDto";
import { VariableSaver } from "by/gamefactory/tools/variableSaver/VariableSaver";
import { AuthTokensDto } from "by/gamefactory/cityversetycoon/networking/data/responses/auth/AuthTokensDto";
import { AuthConstants } from "../AuthConstants";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { Web3Model } from "../../../../../web3/models/Web3Model";
import { ILogger } from "../../../../logger/ILogger";
import { GameInfo } from "../../../../../analytics/GameInfo";
import { IConnectionModel } from "by/gamefactory/cityversetycoon/networking/IConnectionModel";
import { IFirstTimeExperienceModel } from "../../../common/models/IFirstTimeExperienceModel";
import { IRenterPolicyModel } from "../../../common/models/Renters/IRenterPolicyModel";
import { BuildingRightsType } from "../../../../networking/data/responses/user/BuildingRightsType";
import { IGameModel } from "../../../game/models/IGameModel";
import { IFortuneWheelModel } from "../../hourlyBonus/model/IFortuneWheelModel";
import { IInventory } from "../../../../../web3/models/IInventory";
import { ITournamentModel } from "../../../common/models/tournament/ITournamentModel";

@injectable()
export class AuthModel extends Bindable implements IAuthModel {
    private readonly networkClient: INetworkClient;
    private readonly connectionModel: IConnectionModel;
    private readonly guestModel: IGuestModel;
    private readonly variableSaver: VariableSaver;
    private readonly playerModel: IPlayerModel;
    private readonly firstTimeExperienceModel: IFirstTimeExperienceModel;
    private readonly renterPolicyModel: IRenterPolicyModel;
    private readonly fortuneWheelModel: IFortuneWheelModel;
    private readonly tournamentModel: ITournamentModel;
    private readonly gameModel: IGameModel;
    private readonly inventory: IInventory;
    private readonly web3Model: Web3Model;
    private readonly gameInfo: GameInfo;
    private readonly logger: ILogger;

    public constructor(
        @inject("INetworkClient") networkClient: INetworkClient,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        @inject("IGuestModel") guestModel: IGuestModel,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("IFirstTimeExperienceModel") firstTimeExperienceModel: IFirstTimeExperienceModel,
        @inject("IRenterPolicyModel") renterPolicyModel: IRenterPolicyModel,
        @inject("IFortuneWheelModel") fortuneWheelModel: IFortuneWheelModel,
        @inject("IGameModel") gameModel: IGameModel,
        @inject("ITournamentModel") tournamentModel: ITournamentModel,
        @inject("IInventory") inventory: IInventory,
        web3Model: Web3Model,
        gameInfo: GameInfo,
        variableSaver: VariableSaver,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.networkClient = networkClient;
        this.connectionModel = connectionModel;
        this.guestModel = guestModel;
        this.variableSaver = variableSaver;
        this.playerModel = playerModel;
        this.firstTimeExperienceModel = firstTimeExperienceModel;
        this.renterPolicyModel = renterPolicyModel;
        this.fortuneWheelModel = fortuneWheelModel;
        this.gameModel = gameModel;
        this.tournamentModel = tournamentModel;
        this.inventory = inventory;
        this.web3Model = web3Model;
        this.gameInfo = gameInfo;
        this.logger = logger;
        this.connectionModel.propertyChanged.add(this.onConnectionModelPropertyChanged, this);
        this.web3Model.propertyChanged.add(this.onWeb3ModelPropertyChanged, this);
        this.validateAccount();
    }

    public async createWalletAuth(): Promise<boolean> {
        try {
            if (!this.web3Model.connected.value) {
                this.logger.debug("Not connected after modal opened");
                return false;
            }
            if (this.web3Model.address.value == null) {
                this.logger.debug("Unable to authorize wallet, user connected without address");
                return false;
            }
            const nonce = (await this.networkClient.authWeb3Nonce(this.guestModel.id.value, this.web3Model.address.value))?.nonce;
            if (nonce == null) {
                this.logger.error("Unable to get user nonce");
                return false;
            }
            const signature = await this.web3Model.signMessage(nonce);
            if (signature == null || signature === "") {
                this.logger.error("Signature is empty, disconnect");
                await this.web3Model.disconnect();
                return false;
            }
            const result = await this.networkClient.authWeb3(this.web3Model.address.value, signature, this.gameInfo.gameVersion);
            try {
                this.saveAuthTokens(result);
                this.connectionModel.resolveConnection();
                await this.updateAccountProgress();
            }
            catch (e: unknown) {
                this.logger.error("Exception on updating progress for authorized user: ", e);
            }
            return true;
        }
        catch (e: unknown) {
            this.logger.error("Exception on signing with wallet: ", e);
            return false;
        }
    }

    public async requestWeb3Nonce(walletAddress: string): Promise<string> {
        if (!this.web3Model.connected.value) {
            throw new Error("Not connected after modal opened");
        }
        if (walletAddress == null || walletAddress.length === 0) {
            throw new Error("Unable to authorize wallet, user connected without address");
        }
        return (await this.networkClient.authWeb3Nonce(this.guestModel.id.value, walletAddress))?.nonce;
    }

    public async updateAccountProgress(): Promise<void> {
        try {
            await this.playerModel.loadPlayerData();
            await this.playerModel.loadBuildings(false);
            await this.guestModel.loadGuestData();
            await this.firstTimeExperienceModel.getFirstTimeExperience();
            await this.gameModel.getCurrentAction();
            await this.fortuneWheelModel.updateFortuneWheelStatus();
            if (this.playerModel.buildingRights.value === BuildingRightsType.RENTER) {
                await this.renterPolicyModel.updateRenterPolicyStatus();
            }
            await this.tournamentModel.getTournamentsSettings();
            const walletsDto = await this.networkClient.getExternalWallets();
            for (const wallet of walletsDto.items) {
                if (!this.web3Model.hasExternalWalletInInventory(wallet.walletAddress)) {
                    this.web3Model.addExternalWallet(wallet.walletAddress);
                }
            }
        }
        catch (error: unknown) {
            this.logger.error("Exception on updating account progress: ", error);
        }
    }

    private onConnectionModelPropertyChanged(model: IConnectionModel, key: string): void {
        switch (key) {
            case nameof(model, "connected"):
                if (!model.connected.value) {
                    this.logger.debug(`[AuthModel] Connection model not connected, disconnect`);
                    this.disconnectUser();
                }
                break;
        }
    }

    private onWeb3ModelPropertyChanged(model: Web3Model, key: string): void {
        switch (key) {
            case nameof(model, "connected"):
            case nameof(model, "walletAutoConnected"):
                if (!model.connected.value) {
                    this.logger.debug(`[AuthModel] Web3 model not connected, disconnect`);
                    this.disconnectUser();
                }
                break;
        }
    }

    private async disconnectUser(): Promise<void> {
        this.web3Model.disconnect();
        this.connectionModel.disconnect();
    }

    private validateAccount(): void {
        if (!this.variableSaver.exist(AuthConstants.ACCESS_TOKEN) || !this.variableSaver.exist(AuthConstants.REFRESH_TOKEN)) {
            this.logger.debug(`[AuthModel] User without auth tokens, disconnect`);
            this.disconnectUser();
            return;
        }
        // Can be undefined, in this case we should await auto connect result
        if (this.web3Model.walletAutoConnected.value === false && !this.web3Model.connected.value) {
            this.logger.debug(`[AuthModel] Web3 model not auto-connected, disconnect`);
            this.disconnectUser();
            return;
        }

        if (!this.playerModel.authorizedVia.value?.web3) {
            this.logger.debug("[AuthModel] Player not connected with web3, disconnect");
            this.disconnectUser();
        }
    }

    public async refreshToken(): Promise<boolean> {
        try {
            if (!this.variableSaver.exist(AuthConstants.REFRESH_TOKEN)) {
                return false;
            }
            const refreshToken = this.variableSaver.getString(AuthConstants.REFRESH_TOKEN);
            if (refreshToken == null || refreshToken == "") {
                return false;
            }
            const refreshTokenBody = {
                refreshTokenString: refreshToken
            }
            const authTokensDto = await this.networkClient.refreshToken(refreshTokenBody);
            this.validateAuthDto(authTokensDto);
            this.saveAuthTokens(authTokensDto);
            this.connectionModel.resolveConnection();
            return true;
        } catch (error: unknown) {
            this.logger.error("Exception on update refresh token: ", error);
            return false;
        }
    }

    private validateAuthDto(authDto: AuthDto | AuthTokensDto): void {
        if (authDto.accessToken == undefined || authDto.accessToken == "") {
            throw new Error("Failed to get accessToken in response.");
        }
        if (authDto.refreshToken == undefined || authDto.refreshToken == "") {
            throw new Error("Failed to get refreshToken in response.");
        }
    }

    private saveAuthTokens(authDto: AuthDto | AuthTokensDto): void {
        this.variableSaver.saveString(AuthConstants.ACCESS_TOKEN, authDto.accessToken);
        this.variableSaver.saveString(AuthConstants.REFRESH_TOKEN, authDto.refreshToken);
    }
}
